/* eslint-disable jsx-a11y/click-events-have-key-events */
/* eslint-disable jsx-a11y/no-static-element-interactions */
import React, { useEffect, useMemo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import IcomoonReact from 'icomoon-react';
import styled from '@emotion/styled';
import PropTypes from 'prop-types';

import Text from '../../../../shared/_TBComponents/Text';
import iconSet from '../../../../shared/images/teambuildr-selection.json';

import useFeed from '../hooks/useFeed';
import { setIsFeedShowing } from '../../ducks/feedActions';
import { feedTracker } from '../../../../shared/utils/amplitudeHelper';

const BoxContainer = styled('div')`
  display: flex;
  align-items: center;
  height: 100%;
`;

const LowerFeedHeaderDiv = styled('div')`
  display: flex;
  justify-content: space-between;
  margin-top: ${(props) => (props.isTvMode ? '' : '10px')};
  background: #ffffff;
  border-radius: 10px;
  border: ${(props) => (props.isPrimaryDarkTheme && props.isTvMode ? '1px solid #ffffff' : '')};
  box-shadow: ${(props) => (props.isTvMode ? '' : '0px 2px 4px 0px rgb(0 0 0 / 10%)')};
  flex-direction: column;
  user-select: none;
  height: ${(props) => (props.isTvMode ? '41px' : '75px')};
  
  --tv-width: 500px;
  @media screen and (max-width: 768px) { --tv-width: 350px; }
  @media screen and (max-width: 580px) { --tv-width: 250px; }
  @media screen and (max-width: 500px) { --tv-width: 200px; }
  @media screen and (max-width: 420px) { --tv-width: 160px; }
  
  width: ${(props) => (props.isTvMode ? 'var(--tv-width)' : '')};
`;

const TabBox = styled('div')`
  display: flex;
  justify-content: space-between;
  width: 50%;
  flex-direction: column;
  align-items: center;
  height: 100%;
  cursor: pointer;
  border-radius: ${(props) => (props.isTvMode ? '10px' : '')};
  background-color: ${(props) => (props.isTvMode && props.isActive ? '#000000' : '')};
  .border-bottom-div {
    border-bottom: ${(props) => (props.isActive ? '3px' : '0px')} solid
      #444444;
    width: 90%;
  }
  .border-bottom-empty {
    border-bottom-width: 3px;
  }
`;

const TabContent = styled('div')`
  display: flex;
  gap: 10px;
`;

const TAB_CONFIG = {
  left: { icon: 'note', text: 'Posts' },
  right: { icon: 'ribbon', text: 'PRs' },
};

const LowerFeedHeader = ({ scrollToTopOfFeed }) => {
  const { handleFetchFeed } = useFeed();
  const currentUser = useSelector((state) => state.auth.data.currentUser);
  const isFeedShowing = useSelector((state) => state.feed.data.isFeedShowing);
  const isTvMode = useSelector((state) => state.feed.data.isTvMode);
  const { userSkin: skin = {} } = currentUser || {};
  const { darkTheme: isPrimaryDarkTheme = false } = skin || {};

  const dispatch = useDispatch();

  useEffect(() => {
    if (currentUser?.accountCode !== undefined) {
      handleFetchFeed(currentUser.accountCode);
    }
  }, [isFeedShowing, currentUser?.accountCode]);

  const getTabTextColor = useCallback((isActive) => {
    // In TV mode, active tab is white, inactive is black
    if (isTvMode) {
      return isActive ? '#ffffff' : '#444444';
    }
    // In normal mode, active tab is black, inactive is lightgrey
    return isActive ? '#444444' : '#9e9e9e';
  }, [isTvMode]);

  const rightTextColor = useMemo(
    () => getTabTextColor(!isFeedShowing),
    [getTabTextColor, isFeedShowing],
  );
  const leftTextColor = useMemo(
    () => getTabTextColor(isFeedShowing),
    [getTabTextColor, isFeedShowing],
  );

  const handleTabClick = useCallback((isFeedTab) => {
    // clicking tab scrolls back to top and switches to appropriate view
    dispatch(setIsFeedShowing(isFeedTab));
    scrollToTopOfFeed();
    feedTracker(isTvMode ? 'TV Mode' : 'Normal Mode', `${isFeedTab ? 'Posts' : 'PRs'} tab clicked`);
  }, [dispatch, scrollToTopOfFeed, isTvMode]);

  const renderTabContent = useCallback((tabType, isActive, color) => (
    <>
      <div />
      <TabContent textColor={color}>
        <IcomoonReact
          iconSet={iconSet}
          size={22}
          icon={TAB_CONFIG[tabType].icon}
          color={color}
        />
        <Text styledTextProps={{ color, fontSize: 16 }}>
          {TAB_CONFIG[tabType].text}
        </Text>
      </TabContent>
      {isActive && !isTvMode ? (
        <div className='border-bottom-div' />
      ) : (
        <div className='border-bottom-empty' />
      )}
    </>
  ), [isTvMode]);

  const renderTab = useCallback((tabType, isActive, color, onClick) => (
    <TabBox
      onClick={onClick}
      isTvMode={isTvMode}
      isActive={isActive}
      textColor={color}
    >
      {renderTabContent(tabType, isActive, color)}
    </TabBox>
  ), [isTvMode, renderTabContent]);

  const renderLeftTab = useMemo(() => renderTab(
    'left',
    isFeedShowing,
    leftTextColor,
    () => handleTabClick(true),
  ), [renderTab, isFeedShowing, leftTextColor, handleTabClick]);

  const renderRightTab = useMemo(() => renderTab(
    'right',
    !isFeedShowing,
    rightTextColor,
    () => handleTabClick(false),
  ), [renderTab, isFeedShowing, rightTextColor, handleTabClick]);

  return (
    <LowerFeedHeaderDiv
      isTvMode={isTvMode}
      isPrimaryDarkTheme={isPrimaryDarkTheme}
    >
      <BoxContainer>
        {renderLeftTab}
        {renderRightTab}
      </BoxContainer>
    </LowerFeedHeaderDiv>
  );
};

LowerFeedHeader.propTypes = {
  scrollToTopOfFeed: PropTypes.func.isRequired,
};

export default React.memo(LowerFeedHeader);
